<template>
  <div
    class="event-card"
    role="button"
    tabindex="0"
    @click="openEvent"
    @keydown.enter="openEvent"
    @keydown.space.prevent="openEvent"
  >
    <div class="event-cover">
      <img
        v-if="event?.imageURL"
        :src="event.imageURL"
        :alt="event.title || 'Мероприятие'"
        class="event-image"
      />

      <div
        v-else
        class="event-placeholder"
      >
        <div class="placeholder-orb placeholder-orb-first" />
        <div class="placeholder-orb placeholder-orb-second" />

        <span class="placeholder-text">
          EVENT
        </span>
      </div>

      <div class="event-overlay" />

      <div class="event-top">
        <div class="event-type">
          <span class="event-type-dot" />

          Событие
        </div>

        <div class="city-label">
          {{ cityLabel }}
        </div>
      </div>

      <div class="event-title-wrapper">
        <h3 class="event-title">
          {{ event?.title || "Название мероприятия" }}
        </h3>
      </div>
    </div>

    <div class="event-content">
      <div class="event-date-row">
        <div class="calendar-badge">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M7 3V6M17 3V6M4 9H20M5.5 5H18.5C19.33 5 20 5.67 20 6.5V19C20 19.83 19.33 20.5 18.5 20.5H5.5C4.67 20.5 4 19.83 4 19V6.5C4 5.67 4.67 5 5.5 5Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div class="event-date-content">
          <span class="event-date-label">
            Когда
          </span>

          <span class="event-date">
            {{ event?.dateOfEvent || "Дата не указана" }}
          </span>
        </div>
      </div>

      <p
        v-if="event?.description"
        class="event-description"
      >
        {{ event.description }}
      </p>
    </div>

    <div class="event-hover-arrow">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M9 6L15 12L9 18"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue"

const props = defineProps({
  event: {
    type: Object,
    required: true
  }
})

const emit = defineEmits([
  "open-event"
])

const regions = {
  1: "Москва",
  2: "Санкт-Петербург"
}

const cityLabel = computed(() => {
  return (
    regions[Number(props.event?.cityID)]
    || "Регион"
  )
})

const openEvent = () => {
  emit(
    "open-event",
    props.event
  )
}
</script>

<style scoped>
/* =========================================================
   CARD
   ========================================================= */

.event-card {
  --event-background: #ffffff;
  --event-border: rgba(0, 0, 0, 0.07);
  --event-primary-text: #171717;
  --event-secondary-text: #777777;
  --event-meta-background: rgba(91, 91, 230, 0.07);

  position: relative;

  width: 375px;
  height: 258px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  box-sizing: border-box;

  border: 1px solid var(--event-border);
  border-radius: 20px;

  background: var(--event-background);

  cursor: pointer;

  box-shadow:
    0 2px 5px rgba(0, 0, 0, 0.025),
    0 10px 28px rgba(0, 0, 0, 0.05);

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.event-card:hover {
  transform: translateY(-3px);

  border-color: rgba(79, 93, 229, 0.18);

  box-shadow:
    0 6px 12px rgba(0, 0, 0, 0.06),
    0 20px 42px rgba(0, 0, 0, 0.11);
}

/* =========================================================
   THEME
   ========================================================= */

[data-theme="light"] .event-card {
  --event-background: #ffffff;
  --event-border: rgba(0, 0, 0, 0.07);
  --event-primary-text: #171717;
  --event-secondary-text: #747474;
  --event-meta-background: rgba(91, 91, 230, 0.07);
}

[data-theme="dark"] .event-card {
  --event-background: #292929;
  --event-border: rgba(255, 255, 255, 0.08);
  --event-primary-text: #ffffff;
  --event-secondary-text: #a5a5a5;
  --event-meta-background: rgba(110, 103, 242, 0.11);

  box-shadow:
    0 2px 5px rgba(0, 0, 0, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.17);
}

/* =========================================================
   COVER
   ========================================================= */

.event-cover {
  position: relative;

  width: 100%;
  height: 158px;

  flex-shrink: 0;

  overflow: hidden;

  background: #1d1d1d;
}

.event-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transform: scale(1.001);

  transition:
    transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1),
    filter 0.3s ease;
}

.event-card:hover .event-image {
  transform: scale(1.035);
}

/* =========================================================
   PLACEHOLDER
   ========================================================= */

.event-placeholder {
  position: relative;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  background:
    linear-gradient(
      135deg,
      #1c65d7 0%,
      #5855df 48%,
      #9149da 100%
    );
}

.placeholder-text {
  position: relative;
  z-index: 2;

  font-family: "Eastman-Bold";
  font-size: 32px;
  letter-spacing: 8px;

  color: rgba(255, 255, 255, 0.18);
}

.placeholder-orb {
  position: absolute;

  border-radius: 50%;

  filter: blur(8px);
}

.placeholder-orb-first {
  width: 150px;
  height: 150px;

  top: -75px;
  right: -35px;

  background: rgba(255, 255, 255, 0.18);
}

.placeholder-orb-second {
  width: 130px;
  height: 130px;

  bottom: -85px;
  left: 20px;

  background: rgba(255, 255, 255, 0.12);
}

/* =========================================================
   OVERLAY
   ========================================================= */

.event-overlay {
  position: absolute;
  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.18) 0%,
      transparent 38%
    ),
    linear-gradient(
      to top,
      rgba(0, 0, 0, 0.86) 0%,
      rgba(0, 0, 0, 0.48) 34%,
      rgba(0, 0, 0, 0.07) 72%,
      transparent 100%
    );
}

/* =========================================================
   TOP LABELS
   ========================================================= */

.event-top {
  position: absolute;

  top: 12px;
  left: 12px;
  right: 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 8px;
}

.event-type,
.city-label {
  height: 25px;

  display: inline-flex;
  align-items: center;

  box-sizing: border-box;

  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 20px;

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  color: #ffffff;

  font-family: "Eastman-Medium";
  font-size: 10px;
  line-height: 1;
}

.event-type {
  gap: 6px;

  padding: 0 10px;

  background: rgba(15, 15, 15, 0.34);
}

.event-type-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: #ffffff;

  box-shadow:
    0 0 0 3px rgba(255, 255, 255, 0.14);
}

.city-label {
  padding: 0 11px;

  background:
    linear-gradient(
      90deg,
      rgba(34, 118, 230, 0.94),
      rgba(130, 71, 226, 0.94)
    );

  box-shadow:
    0 5px 15px rgba(59, 80, 220, 0.22);
}

/* =========================================================
   TITLE
   ========================================================= */

.event-title-wrapper {
  position: absolute;

  left: 14px;
  right: 14px;
  bottom: 14px;
}

.event-title {
  margin: 0;

  font-family: "Eastman-Medium";
  font-size: 16px;
  line-height: 1.12;
  letter-spacing: -0.2px;

  color: #ffffff;

  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.34);

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  overflow: hidden;
}

/* =========================================================
   CONTENT
   ========================================================= */

.event-content {
  position: relative;

  height: 100px;

  padding: 10px 46px 10px 14px;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;

  background: var(--event-background);
}

/* =========================================================
   DATE
   ========================================================= */

.event-date-row {
  display: flex;
  align-items: center;

  gap: 9px;

  flex-shrink: 0;
}

.calendar-badge {
  width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border-radius: 50%;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3679ec 0%,
      #665fe7 52%,
      #8d4cdd 100%
    );

  box-shadow:
    0 5px 13px rgba(88, 81, 224, 0.22);
}

.calendar-badge svg {
  width: 16px;
  height: 16px;
}

.event-date-content {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 1px;
}

.event-date-label {
  font-family: "Eastman-Medium";
  font-size: 8px;
  line-height: 1.1;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  color: var(--event-secondary-text);

  opacity: 0.72;
}

.event-date {
  font-family: "Eastman-Medium";
  font-size: 12px;
  line-height: 1.2;

  color: var(--event-primary-text);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =========================================================
   DESCRIPTION
   ========================================================= */

.event-description {
  margin: 7px 0 0;

  font-family: "Eastman-Medium";
  font-size: 9.5px;
  line-height: 1.28;

  color: var(--event-secondary-text);

  display: -webkit-box;

  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  overflow: hidden;
}

/* =========================================================
   ARROW
   ========================================================= */

.event-hover-arrow {
  position: absolute;

  right: 14px;
  bottom: 24px;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  color: var(--event-secondary-text);

  background: var(--event-meta-background);

  transition:
    transform 0.22s ease,
    color 0.22s ease,
    background 0.22s ease,
    box-shadow 0.22s ease;
}

.event-hover-arrow svg {
  width: 15px;
  height: 15px;
}

.event-card:hover .event-hover-arrow {
  transform: translateX(2px);

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3679ec,
      #8247e2
    );

  box-shadow:
    0 5px 13px rgba(88, 81, 224, 0.2);
}

/* =========================================================
   FOCUS
   ========================================================= */

.event-card:focus-visible {
  outline: 2px solid #5f70ec;
  outline-offset: 3px;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 420px) {
  .event-card {
    width: 100%;
    max-width: 375px;
  }
}
</style>